/**
 * EL Image Banner - Text Overlay Effects
 * Handles animation and parallax effects for text overlays
 */

if (typeof ElImageBannerOverlay === 'undefined') {
  window.ElImageBannerOverlay = class ElImageBannerOverlay {
    constructor() {
      this.animateSelector = '.el-image-banner__overlay--animate';
      this.parallaxSelector = '[data-el-parallax="true"]';
      this.visibleClass = 'el-is-visible';
      this.ticking = false;
      this.parallaxElements = new Map();
      this.animationObserver = null;
      this.parallaxObserver = null;
      this.scrollHandler = null;

      this.init();
    }

    init() {
      this.initAnimations();
      this.initParallax();
    }

    destroy() {
      if (this.animationObserver) {
        this.animationObserver.disconnect();
        this.animationObserver = null;
      }
      if (this.parallaxObserver) {
        this.parallaxObserver.disconnect();
        this.parallaxObserver = null;
      }
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
      this.parallaxElements.clear();
    }

    initAnimations() {
      const overlays = document.querySelectorAll(this.animateSelector);

      if (overlays.length === 0) return;

      if ('IntersectionObserver' in window) {
        this.animationObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add(this.visibleClass);
              }
            });
          },
          {
            threshold: 0.2,
            rootMargin: '0px 0px -50px 0px',
          }
        );

        overlays.forEach((overlay) => {
          this.animationObserver.observe(overlay);
        });
      } else {
        overlays.forEach((overlay) => {
          overlay.classList.add(this.visibleClass);
        });
      }
    }

    initParallax() {
      const parallaxOverlays = document.querySelectorAll(this.parallaxSelector);

      if (parallaxOverlays.length === 0) return;

      // Track which elements are in viewport
      parallaxOverlays.forEach((overlay) => {
        this.parallaxElements.set(overlay, { inViewport: false, currentY: 0 });
      });

      // Use IntersectionObserver to track viewport visibility
      this.parallaxObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const data = this.parallaxElements.get(entry.target);
            if (data) {
              data.inViewport = entry.isIntersecting;
            }
          });
        },
        {
          threshold: 0,
          rootMargin: '100px 0px 100px 0px',
        }
      );

      parallaxOverlays.forEach((overlay) => {
        this.parallaxObserver.observe(overlay);
      });

      const handleParallax = () => {
        const windowHeight = window.innerHeight;

        parallaxOverlays.forEach((overlay) => {
          const data = this.parallaxElements.get(overlay);
          
          // Only process if element is in viewport
          if (!data || !data.inViewport) return;

          const parent = overlay.parentElement;
          const rect = parent.getBoundingClientRect();
          
          // Calculate position relative to viewport center
          const elementCenter = rect.top + rect.height / 2;
          const viewportCenter = windowHeight / 2;
          const distanceFromCenter = elementCenter - viewportCenter;
          
          // Very subtle movement: max ~7.5px total travel (reduced by half)
          const speed = (parseFloat(overlay.dataset.elParallaxSpeed) || 0.02) / 2;
          const targetY = distanceFromCenter * speed;
          
          // Smooth interpolation (lerp) for fluid movement
          const currentY = data.currentY || 0;
          const newY = currentY + (targetY - currentY) * 0.08;
          
          // Clamp the movement to a small range (reduced by half)
          const clampedY = Math.max(-7.5, Math.min(7.5, newY));
          
          data.currentY = clampedY;
          overlay.style.transform = `translateY(${clampedY}px)`;
        });
      };

      // Initial call
      handleParallax();

      this.scrollHandler = () => {
        if (!this.ticking) {
          window.requestAnimationFrame(() => {
            handleParallax();
            this.ticking = false;
          });
          this.ticking = true;
        }
      };
      window.addEventListener('scroll', this.scrollHandler);
    }
  };

  let _elImageBannerInstance = null;

  function initImageBanners() {
    if (_elImageBannerInstance) {
      _elImageBannerInstance.destroy();
    }
    _elImageBannerInstance = new ElImageBannerOverlay();
  }

  document.addEventListener('DOMContentLoaded', initImageBanners);

  // Re-initialize for Shopify Theme Editor
  document.addEventListener('shopify:section:load', initImageBanners);
}